import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, type HTMLMotionProps } from "framer-motion";
import { cn } from "../../lib/cn";

type PanelAnchor = "top" | "bottom" | "left" | "right" | "center";

interface FloatingPanelProps extends Omit<HTMLMotionProps<"div">, "ref"> {
  open: boolean;
  onClose?: () => void;
  anchor?: PanelAnchor;
  usePortal?: boolean;
  backdrop?: boolean;
  className?: string;
  children: React.ReactNode;
}

const ANCHOR_INITIAL: Record<PanelAnchor, Record<string, number>> = {
  top: { opacity: 0, y: -12, scale: 0.97 },
  bottom: { opacity: 0, y: 12, scale: 0.97 },
  left: { opacity: 0, x: -12, scale: 0.97 },
  right: { opacity: 0, x: 12, scale: 0.97 },
  center: { opacity: 0, scale: 0.95 },
};

const ANCHOR_ANIMATE: Record<PanelAnchor, Record<string, number>> = {
  top: { opacity: 1, y: 0, scale: 1 },
  bottom: { opacity: 1, y: 0, scale: 1 },
  left: { opacity: 1, x: 0, scale: 1 },
  right: { opacity: 1, x: 0, scale: 1 },
  center: { opacity: 1, scale: 1 },
};

const spring = { type: "spring" as const, stiffness: 400, damping: 28, mass: 0.8 };

function FloatingPanel({
  open,
  onClose,
  anchor = "bottom",
  usePortal = false,
  backdrop = false,
  className,
  children,
  ...props
}: FloatingPanelProps) {
  const panel = (
    <AnimatePresence>
      {open && (
        <>
          {backdrop && onClose && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="fixed inset-0 z-40"
              onClick={onClose}
              aria-hidden
            />
          )}
          <motion.div
            initial={ANCHOR_INITIAL[anchor]}
            animate={ANCHOR_ANIMATE[anchor]}
            exit={ANCHOR_INITIAL[anchor]}
            transition={spring}
            className={cn(
              "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-md)]",
              "border border-[var(--border-subtle)]",
              "rounded-[var(--radius-xl)]",
              "shadow-[var(--shadow-lg)]",
              "z-50",
              className,
            )}
            {...props}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (usePortal) {
    return createPortal(panel, document.body);
  }

  return panel;
}

FloatingPanel.displayName = "FloatingPanel";

export { FloatingPanel };
export type { FloatingPanelProps, PanelAnchor };
